/**
 * Operating-terminology overlay API service (ADR-126 Decision 3 +
 * ADR-092 / ADR-051).
 *
 * The `operating` domain is deliberately vertical-agnostic: the model only
 * knows OPERATING / NOT_OPERATING. The user-facing label is a per-tenant
 * *terminology overlay* — K-12 renders OPERATING as "In session", corporate as
 * "Open" / "Business hours", responder as "On watch". This service reads and
 * writes that per-tenant (org/site) overlay so the config UI and downstream
 * surfaces can present the tenant's own vocabulary instead of the raw enum.
 *
 * Attaches at the org (district) or site (school) tier with org→site
 * inheritance, mirroring the calendar and routing-policy attachment shape.
 *
 * Uses the typed `@tiaki/http-client` surface per ADR-113: the overlay
 * endpoints are in the generated OpenAPI schema, so the resource shape and
 * call sites come from `components['schemas']` and
 * `client.GET/POST/PATCH/DELETE` — no `client.ky`, no hand-written shapes.
 * The list endpoint is unpaginated (bare array).
 */

import type { components } from '@tiaki/http-client/types';
import { client } from '../lib/http-client';

function unwrap<T>(result: { data?: T; error?: unknown }): T {
  if (result.error !== undefined) {
    throw result.error;
  }
  return result.data as T;
}

/**
 * Per-tenant labels for the resolved operating states plus the noun used for a
 * scheduled event. Empty/undefined fields fall back to the platform defaults
 * below.
 */
export type OperatingTerminology =
  components['schemas']['OperatingTerminology'];

/**
 * The label fields the UI always supplies for a preset/default. The generated
 * `OperatingTerminology` marks these optional (the serializer allows them to be
 * absent on a fetched row), but these app-owned literals always define them —
 * so `Required` keeps consumers free of redundant `?? ''` fallbacks.
 */
type TerminologyLabels = Required<
  Pick<
    OperatingTerminology,
    'operating_label' | 'not_operating_label' | 'event_label'
  >
>;

/** Platform default overlay (generic) — the fallback when a tenant has none. */
export const DEFAULT_TERMINOLOGY: TerminologyLabels = {
  operating_label: 'Operating',
  not_operating_label: 'Not operating',
  event_label: 'Scheduled event',
};

/** A couple of common vertical presets the UI can offer as one-click starters. */
export const TERMINOLOGY_PRESETS: Record<string, TerminologyLabels> = {
  k12: {
    operating_label: 'In session',
    not_operating_label: 'Not in session',
    event_label: 'Drill',
  },
  corporate: {
    operating_label: 'Open',
    not_operating_label: 'Closed',
    event_label: 'Exercise',
  },
  responder: {
    operating_label: 'On watch',
    not_operating_label: 'Off watch',
    event_label: 'Training',
  },
};

export interface OperatingTerminologyFilters {
  district?: number;
  school?: number;
}

export type OperatingTerminologyWriteInput =
  components['schemas']['OperatingTerminologyRequest'];

/**
 * Resolve the *effective* overlay for a scope, honoring org→site inheritance
 * (site override > org default > platform default). Returns `null` when no
 * tenant overlay exists at either tier (caller applies `DEFAULT_TERMINOLOGY`).
 */
export async function getEffectiveTerminology(
  filters: OperatingTerminologyFilters,
): Promise<OperatingTerminology | null> {
  const result = await client.GET('/api/v2/operating/terminology/effective/', {
    params: { query: filters },
  });
  if (result.response.status === 404) return null;
  return unwrap(result);
}

/**
 * Fetch the overlay row attached directly at this scope (no inheritance).
 * The list endpoint is unpaginated (bare array); we return the first row.
 */
export async function getTerminology(
  filters: OperatingTerminologyFilters,
): Promise<OperatingTerminology | null> {
  const result = await client.GET('/api/v2/operating/terminology/', {
    params: { query: filters },
  });
  const rows = unwrap(result);
  return rows[0] ?? null;
}

/**
 * List every terminology overlay the caller can see (across scopes), for the
 * site-grouped table. Same unpaginated endpoint as `getTerminology`,
 * but returns the full bare array instead of only the first row.
 */
export async function listTerminology(
  filters: OperatingTerminologyFilters = {},
): Promise<OperatingTerminology[]> {
  const result = await client.GET('/api/v2/operating/terminology/', {
    params: { query: filters },
  });
  return unwrap(result);
}

export async function createTerminology(
  data: OperatingTerminologyWriteInput,
): Promise<OperatingTerminology> {
  const result = await client.POST('/api/v2/operating/terminology/', {
    body: data,
  });
  return unwrap(result);
}

export async function updateTerminology(
  id: number,
  data: Partial<OperatingTerminologyWriteInput>,
): Promise<OperatingTerminology> {
  const result = await client.PATCH('/api/v2/operating/terminology/{id}/', {
    params: { path: { id } },
    body: data,
  });
  return unwrap(result);
}

export async function deleteTerminology(id: number): Promise<void> {
  const result = await client.DELETE('/api/v2/operating/terminology/{id}/', {
    params: { path: { id } },
  });
  unwrap(result);
}
